<template>
    <div class="filter-section">
        <div class="font-medium text-sm mb-2">
            {{ computedFilterTitle }}
        </div>
        <div class="max-h-[300px] overflow-y-auto">
            <div
                v-if="showSelectAll"
                class="filter-option select-all-option border-b pb-3 mb-2"
            >
                <VcCheckbox
                    :model-value="isAllSelected"
                    :indeterminate="isIndeterminate"
                    @update:modelValue="handleSelectAll"
                >
                    {{ t('select.all') }}
                </VcCheckbox>
            </div>
            <div
                v-for="option in filterOptions"
                :key="getOptionValue(option)"
                class="filter-option py-1.5 cursor-pointer hover:bg-gray-50"
            >
                <slot
                    name="filter-option"
                    :option="option"
                >
                    <VcCheckbox
                        :model-value="isOptionSelected(option)"
                        @update:modelValue="(checked) => handleOptionChange(option, checked)"
                    >
                        {{ getOptionLabel(option) }}
                    </VcCheckbox>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcCheckbox } from '@voicenter-team/voicenter-ui-plus'

const { t } = useI18n()

export interface FilterOption {
    label: string
    value: string | number
    [key: string]: unknown
}

const props = withDefaults(
    defineProps<{
        filterOptions: Array<FilterOption | string | number>
        selectedValues: Array<string | number>
        filterTitle?: string
        showSelectAll?: boolean
    }>(),
    {
        showSelectAll: true
    }
)

const computedFilterTitle = computed(() => {
    return props.filterTitle || t('common.filters')
})

const emit = defineEmits<{
    (event: 'filter-change', values: Array<string | number>): void
}>()

// Internal functions for option value/label extraction
function getOptionValue (option: FilterOption | string | number): string | number {
    if (typeof option === 'object' && option !== null && 'value' in option) {
        return option.value
    }
    return option
}

function getOptionLabel (option: FilterOption | string | number): string {
    if (typeof option === 'object' && option !== null && 'label' in option) {
        return option.label
    }
    return String(option)
}

// Extract all option values once
const allOptionValues = computed(() =>
    props.filterOptions.map(option => getOptionValue(option))
)

// Create a Set for faster lookups
const selectedValuesSet = computed(() => new Set(props.selectedValues))

const isAllSelected = computed(() => {
    if (allOptionValues.value.length === 0) {
        return false
    }
    return allOptionValues.value.every(optionValue =>
        selectedValuesSet.value.has(optionValue)
    )
})

const isIndeterminate = computed(() => {
    const selectedCount = props.selectedValues.length
    return selectedCount > 0 && selectedCount < allOptionValues.value.length
})

const isOptionSelected = (option: FilterOption | string | number): boolean => {
    return selectedValuesSet.value.has(getOptionValue(option))
}

const handleSelectAll = (checked: boolean | string | number | undefined) => {
    emit('filter-change', checked ? allOptionValues.value : [])
}

const handleOptionChange = (option: FilterOption | string | number, checked: boolean | string | number | undefined) => {
    const value = getOptionValue(option)
    const isChecked = Boolean(checked)

    const newValues = isChecked
        ? [ ...props.selectedValues, value ]
        : props.selectedValues.filter(v => v !== value)

    emit('filter-change', newValues)
}
</script>

<style lang="scss" scoped>
.filter-section {
    .filter-option {
        transition: background-color 0.2s;
    }
}
</style>
